import React, { useState } from 'react'

import {
	UserGroup,
	UserGroupAccessScope,
} from '@matech/thebigpos-sdk'
import { useIntl } from 'react-intl'
import { TableColumn } from 'react-data-table-component'
import { useMutation, useQuery } from '@tanstack/react-query'

import { captureException } from '@sentry/react'
import {
	DEFAULT_SORT_BY,
	DEFAULT_SORT_DIRECTION,
} from '../../../constants/pagination'
import useSearch from '../../../hooks/useSearch'
import queryKeys from '../../../services/queryKeys'
import DataTable from '../../../components/DataTable'
import { formatDate } from '../../../services/helper'
import ModalUserGroupsForm, {
	ACCESS_ITEM_TYPES,
	type UserGroupFormData,
	type UserGroupScopeItem,
} from '../../../components/modals/ModalUserGroupsForm'
import usePagination from '../../../hooks/usePagination'
import { TheBigPOSApi } from '../../../lib/TheBigPOSClient'
import useUserGroupMembers from './hooks/useUserGroupMembers'
import useUserGroupAccessScope from './hooks/useUserGroupAccessScope'
import ModalUserGroupsDelete from './components/ModalUserGroupsDelete'
import type { UserRole } from '../../../components/modals/ModalShareLoan'
import convertGroupMembersToUserRoles from './utils/convertGroupMembersToUserRoles'
import { useAlert } from '../../../hooks'

type UserGroupListItem = Pick<
	UserGroup,
	'id' | 'name' | 'description' | 'createdAt'
>

export type UserGroupItem = UserGroupListItem & {
	totalMembers?: number
	totalAccess?: number
	users?: UserRole[]
	accessItems?: UserGroupScopeItem[]
}

const convertGroupScopesToUserGroupScopeItems = (
	data: UserGroupAccessScope[]
) => {
	return data.map((item) => {
		return {
			id: item.id,
			type: item.scopeType,
			value:
				item.scopeType === ACCESS_ITEM_TYPES.USER
					? item.user?.id
					: item.branch?.id,
			label:
				item.scopeType === ACCESS_ITEM_TYPES.USER
					? item.user?.name
					: item.branch?.name,
		} as UserGroupScopeItem
	})
}

const GroupSharingList = () => {
	const { alert } = useAlert()
	const { formatMessage } = useIntl()

	const [showModalGroup, setShowModalGroup] = useState(false)
	const [showModalDelete, setShowModalDelete] = useState(false)

	const [groupItem, setGroupItem] = useState<
		UserGroupItem | undefined
	>(undefined)
	const [deletingGroup, setDeletingGroup] = useState<
		UserGroupItem | undefined
	>(undefined)

	const {
		searchText,
		debouncedSearchText,
		handleSearchChange,
		handleClearSearchClick,
	} = useSearch()

	const {
		// sortBy,
		sortDirection,
		pageSize,
		pageNumber,
		handleRowsPerPageChange,
		handlePageChange,
		handleSort,
	} = usePagination<UserGroupItem>({
		defaultSortBy: DEFAULT_SORT_BY,
		defaultSortDirection: DEFAULT_SORT_DIRECTION,
	})

	const tableTitle = formatMessage({
		id: 'user_groups.title',
	})

	const queryParams = {
		searchText: debouncedSearchText,
		pageNumber,
		pageSize,
		sortBy: '',
		sortDirection,
	}

	// Query for List
	const {
		data: userGroups,
		refetch: refetchUserGroups,
		isLoading,
	} = useQuery({
		queryKey: [queryKeys.userGroups, queryParams],
		queryFn: async () => {
			const response =
				await TheBigPOSApi.searchUserGroups(queryParams)

			return response.data
		},
		refetchOnWindowFocus: false,
	})

	const groupId = groupItem?.id

	// Query for Get Members from item list
	const { data: groupMembers, isFetching: isFetchingGroupMembers } =
		useQuery({
			enabled: !!groupItem?.id && showModalGroup,
			queryKey: [queryKeys.userGroupsMembers, groupId],
			queryFn: async () => {
				const response = await TheBigPOSApi.getUserGroupMembers(
					groupItem?.id!
				)

				return response.data
			},
			select: convertGroupMembersToUserRoles,
		})

	// Query for Get Scopes from item list
	const { data: groupScopes, isFetching: isFetchingGroupScopes } =
		useQuery({
			enabled: !!groupItem?.id && showModalGroup,
			queryKey: [queryKeys.userGroupsScopes, groupId],
			queryFn: async () => {
				const response = await TheBigPOSApi.getUserGroupAccessScopes(
					groupItem?.id!
				)

				return response.data
			},
			select: convertGroupScopesToUserGroupScopeItems,
		})

	const {
		mutateAsync: removeUserGroupMutateAsync,
		isPending: isPendingRemoveGroup,
	} = useMutation({
		mutationFn: async (groupId: string) => {
			const response = await TheBigPOSApi.deleteUserGroup(groupId)
			return response
		},
	})

	const handleCloseModal = (modalType: 'members' | 'delete') => {
		if (modalType === 'delete') {
			setShowModalDelete(false)
			setDeletingGroup(undefined)
			return
		}

		if (modalType === 'members') setShowModalGroup(false)

		setGroupItem(undefined)
	}

	const {
		createGroupMembers,
		editGroupMembers,
		isSubmittingUserGroupMembers,
	} = useUserGroupMembers(
		refetchUserGroups,
		() => handleCloseModal('members'),
		groupMembers
	)

	const { syncGroupAccessScope, isSubmittingGroupScope } =
		useUserGroupAccessScope(groupScopes)

	const createUserGroup = async (data: UserGroupFormData) => {
		const userGroup = await createGroupMembers(data)
		const groupId = userGroup?.id
		return groupId
	}

	const updateUserGroup = async (
		groupId: string,
		data: UserGroupFormData
	) => {
		await editGroupMembers(groupId, data)
		return groupId
	}

	const handleSubmitUserGroupMembersWrapper = async (
		data: UserGroupFormData
	) => {
		let groupId: string | undefined
		const isEditing = !!groupItem?.id

		try {
			if (!isEditing) {
				groupId = await createUserGroup(data)
			} else {
				groupId = await updateUserGroup(groupItem.id, data)
			}

			if (groupId)
				await syncGroupAccessScope(groupId, data.accessItems)

			refetchUserGroups()

			alert(
				formatMessage({
					id: isEditing
						? 'alerts.user_groups_update_success'
						: 'alerts.user_groups_create_success',
				}),
				{
					severity: 'success',
				}
			)
			handleCloseModal('members')
		} catch (error) {
			captureException(error)

			alert(
				formatMessage({
					id: isEditing
						? 'alerts.user_groups_update_failed'
						: 'alerts.user_groups_create_failed',
				}),
				{
					severity: 'error',
				}
			)
		}
	}

	const isModalGroupMembersVisible =
		showModalGroup &&
		!isFetchingGroupMembers &&
		(!groupItem?.id || !isFetchingGroupScopes)

	const handleAddNewGroup = () => {
		setGroupItem(undefined)
		setShowModalGroup(true)
	}

	const handleEditGroup = async (
		group: UserGroupItem,
		modalType: 'members'
	) => {
		setGroupItem(group)
		if (modalType === 'members') setShowModalGroup(true)
	}

	const handleDeleteGroup = (group: UserGroupItem) => {
		setDeletingGroup(group)
		setShowModalDelete(true)
	}

	const handleConfirmGroupDelete = async () => {
		if (!deletingGroup) return

		try {
			await removeUserGroupMutateAsync(deletingGroup.id)
			refetchUserGroups()
		} finally {
			setShowModalDelete(false)
			setDeletingGroup(undefined)
		}
	}

	const handleCloseGroupDeleteModal = () => {
		setShowModalDelete(false)
		setDeletingGroup(undefined)
	}

	const handleSearchTextChange = (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		handleSearchChange(e)
	}

	const handleClearSearchTextChange = () => {
		handleClearSearchClick()
	}

	const columns: TableColumn<UserGroupItem>[] = [
		{
			name: formatMessage({
				id: 'user_groups.table.columns.group_name',
			}),
			selector: (row) => row.name,
			sortable: true,
		},
		{
			name: formatMessage({
				id: 'user_groups.table.columns.description',
			}),
			selector: (row) => row.description ?? '',
		},
		{
			name: formatMessage({ id: 'global.created' }),
			selector: (row) => formatDate(row.createdAt, false),
			sortable: true,
		},
	]

	const actionItems = [
		{
			name: formatMessage({
				id: 'global.edit',
			}),
			onClick: (_e: React.MouseEvent, row: UserGroupItem) =>
				handleEditGroup(row, 'members'),
		},
		{
			name: formatMessage({
				id: 'global.delete',
			}),
			onClick: (_e: React.MouseEvent, row: UserGroupItem) =>
				handleDeleteGroup(row),
		},
	]

	return (
		<>
			<DataTable
				keyField="id"
				title={tableTitle}
				data={userGroups?.rows || []}
				columns={columns}
				defaultSortAsc={DEFAULT_SORT_DIRECTION !== 'desc'}
				defaultSortFieldId={DEFAULT_SORT_BY}
				onSort={handleSort}
				progressPending={isLoading}
				pagination
				paginationServer
				paginationTotalRows={userGroups?.count}
				actionItems={actionItems}
				addNewBtn={{
					text: formatMessage({
						id: 'user_groups.add_group',
					}),
					onClick: handleAddNewGroup,
				}}
				onSearchChange={handleSearchTextChange}
				searchText={searchText}
				onClearSearchClick={handleClearSearchTextChange}
				onRefreshClick={refetchUserGroups}
				onChangePage={handlePageChange}
				onChangeRowsPerPage={handleRowsPerPageChange}
			/>

			<ModalUserGroupsForm
				visible={isModalGroupMembersVisible}
				onClose={() => handleCloseModal('members')}
				isLoading={isFetchingGroupMembers}
				submit={{
					isLoading:
						isSubmittingUserGroupMembers || isSubmittingGroupScope,
					onSubmit: handleSubmitUserGroupMembersWrapper,
				}}
				initialValues={{
					groupName: groupItem?.name ?? '',
					description: groupItem?.description ?? '',
					selectedUsersRoles: groupMembers,
					initialAccessItems: groupScopes,
				}}
			/>

			<ModalUserGroupsDelete
				visible={showModalDelete}
				onClose={handleCloseGroupDeleteModal}
				onSubmit={handleConfirmGroupDelete}
				isSubmitting={isPendingRemoveGroup}
				group={deletingGroup}
			/>
		</>
	)
}

export default GroupSharingList
